// frontend/src/pages/employees/LeavePanel.jsx
import React from "react";
import { hours as fmtHours } from "./format";
import { useAnalytics } from "./useAnalytics";

const panel = {
  background: "white", borderRadius: 12, padding: 24,
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
};
const heading = {
  fontSize: 12, fontWeight: 600, color: "#6b7280",
  textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6,
};

function pick(rows, employeeId) {
  if (!rows || rows.length === 0) return null;
  // When a subject is named, a miss must stay a miss. Both `leave/` and
  // `stat/` can legitimately have no row for THIS employee (no hire date,
  // or inactive) while other rows are present -- falling back to rows[0]
  // would render a colleague's real figure under this employee's name and
  // heading. Only the no-subject case (nobody named, see below) falls
  // back to the first row.
  if (!employeeId) return rows[0];
  return rows.find((r) => r.user_id === Number(employeeId)) || null;
}

/**
 * Vacation and stat for one person.
 *
 * These two numbers live here rather than in the tile row because they
 * do not measure the period the tiles measure: vacation accrues over
 * each employee's own hire anniversary year and is readable only AS AT
 * a date, and stat is credited per holiday. One caption at the top names
 * the window they share. See the spec, section 4 and decision 8.
 *
 * Both endpoints already scope themselves through _visible_employees(),
 * returning exactly one row for a non-manager, so this component needs
 * no gate of its own.
 */
export default function LeavePanel({ employeeId, asOf, startDate, endDate }) {
  const leave = useAnalytics("leave/", { as_of: asOf });
  const stat = useAnalytics("stat/", {
    start_date: startDate, end_date: endDate,
  });

  const row = pick(leave.data && leave.data.rows, employeeId);
  const missing = (leave.data && leave.data.missing_hire_date) || [];

  const holidays = ((stat.data && stat.data.holidays) || []).map((h) => {
    const mine = pick(h.employees, employeeId);
    return { date: h.date, name: h.name, credit: mine ? mine.credit : "0.00" };
  });
  const statTotal = holidays.reduce((s, h) => s + Number(h.credit || 0), 0);

  return (
    <div style={panel}>
      <h3 style={{ marginTop: 0, marginBottom: 2, fontSize: 16 }}>Leave</h3>
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 20 }}>
        As at {asOf}
      </div>

      {leave.loading && <div style={{ color: "#6b7280" }}>Loading…</div>}
      {leave.error && (
        <div role="alert" style={{ color: "#b91c1c" }}>{leave.error}</div>
      )}

      {!leave.loading && !leave.error && !row && (
        <div style={{ background: "#fffbeb", border: "1px solid #fde68a",
                      borderRadius: 8, padding: "10px 12px", fontSize: 13,
                      color: "#92400e" }}>
          {missing.length > 0
            ? `No hire date recorded for ${missing.join(", ")}, so no vacation
               can be calculated. Add it on the Settings page.`
            : "No vacation figures available."}
        </div>
      )}

      {row && (
        <div style={{ marginBottom: 24 }}>
          <div style={heading}>Vacation balance</div>
          <div data-testid="vacation-balance"
               style={{ fontSize: 28, fontWeight: 700, color: "#111827" }}>
            {fmtHours(row.balance)} h
          </div>
          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>
            Earned {fmtHours(row.earned)} · Taken {fmtHours(row.taken)}
          </div>
        </div>
      )}

      <div style={heading}>Stat credited</div>
      {stat.loading && <div style={{ color: "#6b7280" }}>Loading…</div>}
      {stat.error && (
        <div role="alert" style={{ color: "#b91c1c" }}>{stat.error}</div>
      )}

      {!stat.loading && !stat.error && (
        <>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#111827",
                        marginBottom: 10 }}>
            {fmtHours(statTotal)} h
          </div>
          {holidays.length === 0 ? (
            <div style={{ color: "#6b7280", fontSize: 13 }}>
              No statutory holidays in this period.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse",
                            fontSize: 13 }}>
              <tbody>
                {holidays.map((h) => (
                  <tr key={h.date}>
                    <td style={{ padding: "5px 2px" }}>{h.name}</td>
                    <td style={{ padding: "5px 2px", textAlign: "right",
                                 color: Number(h.credit) > 0
                                   ? "#111827" : "#9ca3af" }}>
                      {Number(h.credit) > 0 ? fmtHours(h.credit) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
